import { FC, useState, useEffect } from 'react';
import RowSkeleton from 'wa-storybook/components/skeletons/components/row-skeleton/row-skeleton';

import Button from 'wa-storybook/components/general/button/button';
import styles from './select-interests.module.scss';
import { InterestGroup as InterestGroupT } from 'graphql-types';
import InterestGroup from './interest-group/interest-group';

type Props = {
  isLoading?: boolean;
  interests?: InterestGroupT[] | null;
  onContinue: (selectedInterests: string[], selectedGroups: string[]) => void;
};

const SelectInterestsScreen: FC<Props> = ({
  interests,
  onContinue,
  isLoading,
}: Props) => {
  const [selectedInterests, setSelectedInterests] = useState<{
    [key: string]: string[];
  }>({});

  useEffect(() => {
    const updatedInterests = interests?.reduce((acc, group) => {
      group.interests.forEach(interest => {
        if (interest.selected) {
          if (!acc[group.id]) {
            acc[group.id] = [];
          }

          acc?.[group.id]?.push(interest.id);
        }
      });

      return acc;
    }, selectedInterests);

    setSelectedInterests({ ...(updatedInterests || {}) });
  }, [interests]);

  const onSelectInterest = (
    groupId: string,
    id: string,
    isSelected?: boolean,
  ) => {
    if (isSelected) {
      setSelectedInterests(prev => {
        return {
          ...prev,
          [groupId]: prev[groupId] ? [...prev[groupId], id] : [id],
        };
      });
    } else {
      setSelectedInterests(prev => {
        const updated = { ...prev };
        if (updated[groupId]) {
          updated[groupId] = updated[groupId].filter(
            interest => interest !== id,
          );
        }
        return updated;
      });
    }
  };

  const onContinueClick = () => {
    const data = Object.keys(selectedInterests).reduce(
      (acc, groupId) => {
        const interests = selectedInterests[groupId];
        if (interests?.length) {
          acc.interests = [...acc.interests, ...interests];
          acc.groups = [...acc.groups, groupId];
        }

        return acc;
      },
      { interests: [], groups: [] } as {
        interests: string[];
        groups: string[];
      },
    );

    onContinue(data.interests, data.groups);
  };

  const hasSelectedInterests = Object.keys(selectedInterests).some(
    groupId => selectedInterests[groupId]?.length,
  );

  return (
    <section>
      <h1 className={styles.title}>
        {polyglot.t('home.interests.set_screen.title_1')}
      </h1>

      <p className={styles.subtitle}>
        {polyglot.t('home.interests.set_screen.description_1')}
      </p>

      {isLoading ? (
        <>
          <RowSkeleton rowNumber={1} height={18} />
          <RowSkeleton rowNumber={6} height={12} />
        </>
      ) : (
        <div className={styles['interests-container']}>
          {interests?.map(group => {
            return (
              <InterestGroup
                onSelect={(id, isSelected) =>
                  onSelectInterest(group.id, id, isSelected)
                }
                key={group.id}
                group={group}
              />
            );
          })}
        </div>
      )}

      <p className={styles.info}>
        {polyglot.t('home.interests.set_screen.info')}
      </p>

      <div className={styles[`button-container`]}>
        <Button
          isDisabled={!hasSelectedInterests}
          isLoading={isLoading}
          text={polyglot.t('home.interests.continue')}
          onClick={onContinueClick}
        />
      </div>
    </section>
  );
};

export default SelectInterestsScreen;
